/**
 * Multi-select source-lesson filter.
 *
 * "Concepts from THAT lesson" - the user's top mental model for
 * scoping the graph. Options come from `useSourceLessonOptions()`
 * (lessons that have contributed concepts to mindspace), with a
 * type-ahead filter for libraries with many lessons.
 */

import { useMemo, useState } from 'react';
import { BookOpen, Search } from 'lucide-react';
import { useSourceLessonOptions } from '@/hooks/mindspace/useMindspaceOptions';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import { TapCheckbox } from '@/components/core/common/TapCheckbox';
import { FilterSection } from '@/components/features/mindspace/filters/FilterSection';
import { useFilterLayout } from '@/components/features/mindspace/filters/filterLayout';

/** Lessons the phone sheet lists before "Show all N lessons". */
const SHEET_PREVIEW = 5;

interface SourceLessonFilterProps {
  /** Section open on first render. Story-only hatch, initial state only — no
   *  production call site passes it, and the panels all mount collapsed. It
   *  exists because the section body (chips, window pills, the lesson list) is
   *  otherwise reachable only by clicking, and a click-only state has no resting
   *  story to diff. Same escape hatch as `FlowSelector`/`SearchInput`'s
   *  `defaultOpen`. */
  defaultOpen?: boolean;
}

export function SourceLessonFilter({
  defaultOpen = false,
}: SourceLessonFilterProps) {
  const { selectedLessons, setFilters } = useTagFilters();
  const { data: options = [], isLoading } = useSourceLessonOptions();
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');
  const sheet = useFilterLayout() === 'sheet';

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter(o => o.title.toLowerCase().includes(needle));
  }, [options, search]);

  const toggle = (id: string) => {
    const next = selectedLessons.includes(id)
      ? selectedLessons.filter(l => l !== id)
      : [...selectedLessons, id];
    setFilters({ lessons: next });
  };

  const clear = () => setFilters({ lessons: [] });

  return (
    <FilterSection
      icon={<BookOpen className='h-3.5 w-3.5' />}
      label={sheet ? 'Lessons' : 'Source lessons'}
      count={selectedLessons.length}
      open={open}
      onOpenChange={setOpen}
      onClear={selectedLessons.length ? clear : undefined}
    >
      {/* No selected-chip stack here - the checkbox ✓ in the list below
          is the canonical control, and the panel-level Active filters
          summary already shows what's on. Two extra renders of the same
          state was noise. */}

      {/* Type-ahead search (only when there are >6 options to choose from) */}
      {options.length > 6 && (
        <LessonSearch value={search} onChange={setSearch} />
      )}

      {isLoading ? (
        <p className='py-1 text-xs text-muted-foreground'>Loading lessons…</p>
      ) : filtered.length === 0 ? (
        <p className='py-1 text-xs text-muted-foreground'>
          {options.length === 0
            ? 'No lessons yet'
            : 'No lessons match your search'}
        </p>
      ) : (
        <LessonList
          lessons={filtered}
          selected={selectedLessons}
          onToggle={toggle}
          sheet={sheet}
          searching={!!search.trim()}
        />
      )}
    </FilterSection>
  );
}

/**
 * One lesson. The whole row is the label, so the whole row takes the tap; in
 * the sheet it is 44px with the box at the trailing edge, as a phone's
 * settings rows are.
 */
function LessonRow({
  title,
  checked,
  onToggle,
  sheet,
}: {
  title: string;
  checked: boolean;
  onToggle: () => void;
  sheet: boolean;
}) {
  return (
    <li>
      <label
        className={
          sheet
            ? 'flex min-h-11 cursor-pointer items-center justify-between gap-3 py-2 text-sm'
            : 'flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs hover:bg-muted/50'
        }
      >
        {sheet ? (
          <TapCheckbox
            checked={checked}
            onChange={onToggle}
            className='order-last'
          />
        ) : (
          <input
            type='checkbox'
            checked={checked}
            onChange={onToggle}
            className='h-3 w-3 rounded-sm border-border accent-primary'
          />
        )}
        <span
          className={
            sheet ? 'min-w-0 text-foreground' : 'truncate text-foreground/90'
          }
        >
          {title}
        </span>
      </label>
    </li>
  );
}

/**
 * The few lessons the sheet lists before "Show all", the picked ones first so
 * a selection is never below the cut - rather than capping the list in a
 * scroller of its own inside the sheet's.
 */
function previewLessons<T extends { id: string }>(
  lessons: T[],
  selected: string[]
): T[] {
  const picked = (l: T) => Number(selected.includes(l.id));
  return [...lessons]
    .sort((a, b) => picked(b) - picked(a))
    .slice(0, SHEET_PREVIEW);
}

/** Type-ahead over the lesson titles, shown only past six lessons. */
function LessonSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className='relative mb-2'>
      <Search className='pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground' />
      <input
        type='text'
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder='Filter lessons…'
        // `globals.css` puts `ring-2 ring-ring ring-offset-2` on every
        // focus-visible input via a base-layer rule - a harsh halo
        // around a text field. Override with `!important` to suppress;
        // border color (indigo on focus) is the focus indicator.
        // Same pattern as TagChipPicker and ConceptSearchInput.
        className='w-full rounded-sm border border-border bg-background py-1 pl-7 pr-2 text-xs text-foreground !outline-none placeholder:text-muted-foreground/60 focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 dark:focus:border-primary max-sm:h-11 max-sm:rounded-md max-sm:pl-8 max-sm:text-base'
      />
    </div>
  );
}

/** The lessons as rows; in the sheet, a preview and "Show all N lessons". */
function LessonList({
  lessons,
  selected,
  onToggle,
  sheet,
  searching,
}: {
  lessons: Array<{ id: string; title: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  sheet: boolean;
  searching: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const shown =
    sheet && !showAll && !searching
      ? previewLessons(lessons, selected)
      : lessons;
  return (
    <>
      <ul
        className={
          sheet
            ? 'divide-y divide-border'
            : 'max-h-48 space-y-0.5 overflow-y-auto'
        }
      >
        {shown.map(opt => (
          <LessonRow
            key={opt.id}
            title={opt.title}
            checked={selected.includes(opt.id)}
            onToggle={() => onToggle(opt.id)}
            sheet={sheet}
          />
        ))}
      </ul>
      {shown.length < lessons.length && (
        <button
          type='button'
          onClick={() => setShowAll(true)}
          className='mt-1 flex h-11 w-full items-center text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          Show all {lessons.length} lessons
        </button>
      )}
    </>
  );
}
